import * as React from 'react';
import {
  AgencyDownlineRepository,
  AgencyRepository,
  GlobSunfireOrgRepository,
  UserSunfireRepository,
  WebAPI,
} from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';
import type { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';
import type { GlobSunfireOrg } from '../../MedicareProAPI/Entities/Glob/GlobSunfireOrg';
import type { UserSunfire } from '../../MedicareProAPI/Entities/User/UserSunfire';
import StyledModal from './StyledModal';

interface AgencySunFireSetupProps {
  userAccessToken: string;
  userId: number;
  agencyId: number;
  thisAgency: Agency;
  thisSunFire?: GlobSunfireOrg;
}

type SfUI = { enabled: boolean; id?: number; pending?: boolean };

/**
 * Panel for setting up agency SunFire — with paginated downlines
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-30
 * @lastUpdated 2025-10-30
 * @version 1.2.0 (adds pagination + fixes)
 */
export const AgencySunFireSetup = ({
  userAccessToken,
  userId,
  agencyId,
  thisAgency,
  thisSunFire,
}: AgencySunFireSetupProps): React.JSX.Element => {
  const [agency, setAgency] = React.useState<Agency>(thisAgency);
  const [sunFire, setSunFire] = React.useState<GlobSunfireOrg | null>(thisSunFire);
  const [sunFireData, setSunFireData] = React.useState<any>({});
  const [originalSunFireData, setOriginalSunFireData] = React.useState<any>({});
  const [downlines, setDownlines] = React.useState<AgencyDownline[]>([]);

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const PAGE_SIZE = 50;

  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const lastFetchedOffsetRef = React.useRef<number>(-1);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const [autoCnx, setAutoCnx] = React.useState<boolean>(agency.getAutoCnxAccess());
  const [cnxLocal, setCnxLocal] = React.useState<any>({});
  const [sunFireOrgId, setSunFireOrgId] = React.useState<number | null>(thisSunFire?.getId());
  const [sunfireOrgOptions, setSunfireOrgOptions] = React.useState<Array<GlobSunfireOrg>>([]);
  const [manage, setManage] = React.useState<boolean>(false);

  const agencyRepository = React.useMemo(() => {
    const r = new AgencyRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const agencyDownlineRepository = React.useMemo(() => {
    const r = new AgencyDownlineRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userSunfireRepository = React.useMemo(() => {
    const r = new UserSunfireRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const globSunfireOrgRepository = React.useMemo(() => {
    const r = new GlobSunfireOrgRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const activeDownlineRows = React.useMemo(() => {
    const keep = new Set(['A', 'T']);
    return downlines.filter((d) => keep.has(d.getUser().getUserStatus()));
  }, [downlines]);

  /**
   * Reset pagination and load the first page when identity changes.
   *
   * @dependency agencyId, userId
   */
  React.useEffect(() => {
    setDownlines([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    lastFetchedOffsetRef.current = -1;

    void fetchDownlines(0, true);
  }, [agencyId, userId]);

  /**
   * Sync SunFire form state with the currently selected org.
   *
   * @dependency sunFire
   */
  React.useEffect(() => {
    if (!sunFire) {
      const blank = { id: '', orgName: '', partnerId: '', app: '', appId: '' };
      setSunFireData(blank);
      setOriginalSunFireData(blank);
    } else {
      handleMakeSunFire(sunFire);
    }
  }, [sunFire]);

  /**
   * Fetch a page of downlines using offset/limit pagination and merge into state.
   *
   * @param startOffset - Zero-based offset to start fetching from
   * @param initial - When true, replaces the list; otherwise appends to it
   * @returns Promise<void>
   *
   */
  const fetchDownlines = React.useCallback(
    async (startOffset: number, initial = false): Promise<void> => {
      // prevent duplicate fetch for the same offset
      if (loadingMore || startOffset === lastFetchedOffsetRef.current) return;

      setLoadingMore(true);
      lastFetchedOffsetRef.current = startOffset;

      try {
        const opts: any = {
          include: ['userSunfire'],
          limit: PAGE_SIZE,
          offset: startOffset,
          sortBy: 'userId',
        };
        const res: any = await agencyDownlineRepository.getAgencyDownlines(userId, agencyId, opts);

        const items: AgencyDownline[] = res.getItems();

        if (initial) setDownlines(items);
        else if (items.length) setDownlines((prev) => prev.concat(items));

        const total: number = res.getTotal();

        // if no items came back, stop trying further pages
        if (items.length === 0) {
          setHasMore(false);
          return;
        }

        const next = startOffset + items.length;
        setOffset(next);
        setHasMore(next < total);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [agencyDownlineRepository, agencyId, userId, loadingMore]
  );

  /**
   * Request the next page of downlines if available.
   *
   * @returns void
   */
  const onLoadMore = React.useCallback((): void => {
    if (!hasMore || loadingMore) return;
    void fetchDownlines(offset, false);
  }, [hasMore, loadingMore, offset, fetchDownlines]);

  /**
   * Attach an infinite-scroll observer to the sentinel element.
   *
   * @returns void
   */
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries, obs) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          obs.unobserve(entry.target);
          onLoadMore();
          queueMicrotask(() => el && obs.observe(el));
        }
      },
      { rootMargin: '1200px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore]);

  /**
   * Attach an infinite-scroll observer to the sentinel element.
   */
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries, obs) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          obs.unobserve(entry.target); // pause to prevent rapid retriggers
          onLoadMore();

          queueMicrotask(() => el && obs.observe(el));
        }
      },
      { rootMargin: '1200px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore]);

  /**
   * Flattens a SunFire org entity into an editable snapshot
   * and seeds both the live and pristine form state.
   *
   * @param sf - SunFire parent organization entity
   * @returns void
   */
  const handleMakeSunFire = (sf: GlobSunfireOrg) => {
    const _sunFire = {
      id: sf.getId(),
      orgName: sf.getOrgName(),
      partnerId: sf.getPartnerId(),
      app: sf.getApp(),
      appId: sf.getAppId(),
    };

    setSunFireData(_sunFire);
    setOriginalSunFireData(_sunFire);
  };

  /**
   * Persist an agency flag update and mirror the value locally.
   *
   * @param patch - Partial agency fields to update (e.g., { autoCnxAccess: boolean })
   * @returns Promise<void>
   */
  const updateAgencyFlag = async (patch: any): Promise<void> => {
    if (!agency) return;
    await agencyRepository.updateAgency(userId, agencyId, patch);
    setAutoCnx(patch.autoCnxAccess);
  };

  /**
   * Upsert the agency's SunFire organization and sync local state/UI.
   *
   * @param value - Selected SunFire org ID, or null to clear
   * @returns Promise<void>
   */
  const handleUpsertSunFire = async (value: number | null): Promise<void> => {
    setIsSaving(true);
    try {
      await agencyRepository.updateAgency(userId, agencyId, { sunfireOrgId: value });
      setIsSaving(false);
      setSunFireOrgId(value);
      setModalOpen(false);

      if (value) {
        const org = sunfireOrgOptions.find((item) => item.getId() === value) || null;
        setSunFire(org);
        if (org) handleMakeSunFire(org);
      } else {
        setSunFire(null);
      }
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      alert('There was a problem saving SunFire. Please try again.');
    }
  };

  /**
   * Optimistically toggle a user's SunFire link on/off and sync with the server.
   *
   * @param d - Agency downline row for the user
   * @param checked - Desired toggle state (true = enable, false = disable)
   * @returns Promise<void>
   */
  const toggleSunFireForUser = async (d: AgencyDownline, checked: boolean): Promise<void> => {
    const u = d.getUser();
    const key = u.getUserId();

    if (checked) {
      if (!sunFireOrgId) return;
      setCnxLocal((m: any) => ({
        ...m,
        [key]: { ...(m[key] || {}), enabled: true, pending: true },
      }));
      try {
        const resp = await userSunfireRepository.addUserSunfire(u.getUserId(), {
          sunfireOrgId: sunFireOrgId,
        });
        const newId = resp.getId();
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: true, id: newId, pending: false },
        }));
      } catch (e) {
        console.error(e);
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: false, id: undefined, pending: false },
        }));
      }
    } else {
      const server = getUserSunfire(d);
      const id = cnxLocal[key]?.id ?? server?.getId?.();
      if (!id) {
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { enabled: false, id: undefined, pending: false },
        }));
        return;
      }
      try {
        await userSunfireRepository.deleteUserSunfire(u.getUserId(), id);
      } catch (e) {
        console.error(e);
      } finally {
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { enabled: false, id: undefined, pending: false },
        }));
      }
    }
  };

  /**
   * Open the SunFire org selector and load available organizations.
   *
   * @returns Promise<void>
   */
  const openSunfireModal = async (): Promise<void> => {
    setModalOpen(true);
    try {
      const orgs = await globSunfireOrgRepository.getGlobSunfireOrgs();
      setSunfireOrgOptions(orgs.getItems());
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Get the first SunFire link for a downline user, if any.
   *
   * @param d - Agency downline row
   * @returns The user's SunFire entity or undefined
   */
  const getUserSunfire = (d: AgencyDownline): UserSunfire | undefined =>
    d.getUser().getUserSunfire().getItems()[0] as UserSunfire | undefined;

  /**
   * Compute the UI state for a downline row by merging server entity and local edits.
   *
   * @param d - Agency downline row
   * @returns SfUI describing toggle state and id
   */
  const getRowState = (d: AgencyDownline): SfUI => {
    const u = d.getUser();
    const key = u.getUserId();
    const server = getUserSunfire(d);
    const local = cnxLocal[key];

    // base from server
    const base: SfUI = {
      enabled: !!server,
      id: server?.getId?.(),
    };

    // overlay local edits if present
    return local ? { ...base, ...local } : base;
  };

  return (
    <>
      <div className="col-12 px-2 sm-resp" id="contact">
        <div className="card cardAddress">
          <div className="card-body mt-0" id="rxCards">
            <div className="row">
              <div className="col-12">
                <img
                  className="rounded ml-2 mt-3"
                  height="45"
                  src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/sunfire.png"
                  alt="SunFire"
                />
              </div>

              <div className="col-12 mt-3 mb-5">
                <button
                  type="button"
                  className="btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow"
                  style={{ borderRadius: 15, background: 'none' }}
                  onClick={() => setManage(!manage)}
                >
                  <b>Manage</b>
                </button>
              </div>

              {manage && (
                <>
                  {/* LEFT: Authorized Organization */}
                  <div className="col-lg-6 col-12 border-right">
                    <div className="mb-2">
                      <b>Authorized Organization</b>
                    </div>

                    <div className="alert alert-sm alert-secondary py-1 mb-3">
                      <i className="fal fa-comment-exclamation mr-2" />
                      Select the parent organization that your agency uses SunFire Matrix through.
                    </div>

                    {sunFire ? (
                      <>
                        <div className="border border-success rounded p-3 font-weight-bold mr-2">
                          <i className="far fa-check-circle mr-1" />
                          {sunFire.getOrgName()}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger mr-2 mt-2"
                          onClick={() => handleUpsertSunFire(null)}
                        >
                          <i className="fal fa-trash" />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={openSunfireModal}
                      >
                        Add SunFire Parent Org
                      </button>
                    )}
                  </div>

                  {/* RIGHT: Broker Access */}
                  <div className="col-lg-6 col-12">
                    <div className="mb-2">
                      <b>Broker Access</b>
                    </div>

                    {sunFire && (
                      <>
                        <div className="alert alert-sm alert-secondary py-1 mb-3">
                          <i className="fal fa-comment-exclamation mr-2" />
                          Enable/disable downline agent access to SunFire. If disabled, the broker
                          will not see a SunFire enrollment option from a client profile page.
                        </div>

                        <div className="row align-items-center mb-3">
                          <div className="col-12">
                            <div className="d-flex align-items-center border-bottom pb-3">
                              <div className="mr-3">
                                <label className="switch mb-0">
                                  <input
                                    type="checkbox"
                                    checked={autoCnx}
                                    onChange={(e) =>
                                      updateAgencyFlag({ autoSunfireAccess: e.target.checked })
                                    }
                                  />
                                  <span className="slider round pt-1" />
                                </label>
                              </div>
                              <div className="mr-auto">
                                <strong>Enable/disable auto SunFire access</strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          className="border rounded"
                          style={{ maxHeight: 477, overflowY: 'auto' }}
                        >
                          {activeDownlineRows.map((d) => {
                            const u = d.getUser();
                            const sf = getRowState(d); // SfUI { enabled, id, pending? }
                            return (
                              <div
                                key={`sf-${u.getUserId()}`}
                                className="row border-bottom pb-1 pt-2 mx-0"
                              >
                                <div className="col-8 font-weight-bold">
                                  {u.getUserFirstname()} {u.getUserLastname()}
                                </div>
                                <div className="col-4 text-right">
                                  <label className="switch mb-0">
                                    <input
                                      type="checkbox"
                                      checked={sf.enabled}
                                      onChange={(e) => toggleSunFireForUser(d, e.target.checked)}
                                    />
                                    <span className="slider round pt-1" />
                                  </label>
                                  {sf.pending && (
                                    <span className="ml-2 small text-muted">
                                      <i className="fal fa-spinner fa-spin" /> linking…
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {activeDownlineRows.length === 0 && !loading && (
                            <div className="p-3 text-muted">No brokers found.</div>
                          )}

                          {loadingMore && (
                            <div className="p-2 text-center">
                              <i className="fal fa-spinner fa-spin" /> Loading…
                            </div>
                          )}

                          {!loadingMore && hasMore && (
                            <div className="p-2 text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={onLoadMore}
                              >
                                Load more
                              </button>
                            </div>
                          )}

                          <div ref={sentinelRef} />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <StyledModal
        size="md"
        title="Select Organization"
        footerCloseButton
        show={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <label className="label">Select Organization</label>
        <select
          className="form-control form-control-sm"
          value={sunFireOrgId ?? ''}
          onChange={(e) => setSunFireOrgId(Number(e.target.value))}
        >
          <option value="">Select…</option>
          {sunfireOrgOptions.map((o) => (
            <option key={o.getId()} value={o.getId()}>
              {o.getOrgName()}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn btn-sm btn-success mt-2"
          onClick={() => handleUpsertSunFire(Number(sunFireOrgId))}
          disabled={isSaving || (!sunFireOrgId && sunFireOrgId !== 0)}
        >
          {isSaving ? 'saving...' : 'Save'}
        </button>
      </StyledModal>
    </>
  );
};
